import { TextFieldModule } from '@angular/cdk/text-field'
import { CommonModule } from '@angular/common'
import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'

import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { last, slice } from 'lodash/fp'
import { MenuModule } from 'primeng/menu'
import { TooltipModule } from 'primeng/tooltip'

import { debounceTime, distinctUntilChanged } from 'rxjs/operators'

import {
  FileExtension,
  FileMimeTypeLabel,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { ShortcutIconName } from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { resetInputFileValueFromEvent } from '@flowx/designer/modules/new-ai/common/ai.utils'
import {
  AiShortcutComponent,
  IconType,
  ShortcutConfig,
} from '@flowx/designer/modules/new-ai/modules/command-palette/components/shortcut/shortcut.component'
import { AiActions } from '@flowx/designer/modules/new-ai/store/ai.actions'
import { AiSelectors } from '@flowx/designer/modules/new-ai/store/ai.selectors'
import { FlxAutofocusDirective } from '@flowx/designer/shared/directives/autofocus.directive'
import { FlxIconModule } from '@flowx/angular-ui-toolkit'

@Component({
  selector: 'flx-ai-type-area',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlxIconModule,
    TextFieldModule,
    AiShortcutComponent,
    TooltipModule,
    MenuModule,
    FlxAutofocusDirective,
  ],
  templateUrl: './type-area.component.html',
  styleUrls: ['./type-area.component.scss'],
})
export class AiTypeAreaComponent implements OnInit {
  #store = inject(Store)
  #actions$ = inject(Actions)
  #destroyRef = inject(DestroyRef)

  #imageFiles = this.#store.selectSignal(AiSelectors.uploadedImageFiles)
  #bpmnFiles = this.#store.selectSignal(AiSelectors.uploadedBpmnFiles)

  #bpmnFilesSelected = computed(() => !!this.#bpmnFiles().length)

  hint = input<string>('')
  currentValue = output<string>()
  textarea = viewChild<ElementRef>('textarea')
  imageUpload = viewChild<ElementRef>('imageUpload')
  docUpload = viewChild<ElementRef>('docUpload')
  processUpload = viewChild<ElementRef>('processUpload')

  typeControl = new FormControl('', [Validators.required, Validators.maxLength(10000)])

  submitShortcut: ShortcutConfig['icons'] = [
    { type: IconType.ICON, name: ShortcutIconName.ArrowUp },
  ]

  showUpload = this.#store.selectSignal(AiSelectors.isUploadAllowed)
  isUploadDisabled = this.#store.selectSignal(AiSelectors.isUploadDisabled)
  allowedFileTypes = this.#store.selectSignal(AiSelectors.allowedFileTypes)
  allowedDocumentFileTypes = this.#store.selectSignal(AiSelectors.allowedDocumentFileTypes)
  allowedImageFileTypes = this.#store.selectSignal(AiSelectors.allowedImageFileTypes)
  allowedProcessFileTypes = this.#store.selectSignal(AiSelectors.allowedProcessFileTypes)
  isMultipleDocumentUploadAllowed = this.#store.selectSignal(
    AiSelectors.isMultipleDocumentUploadAllowed
  )
  isMultipleProcessUploadAllowed = this.#store.selectSignal(
    AiSelectors.isMultipleProcessUploadAllowed
  )
  isMultipleImageUploadAllowed = this.#store.selectSignal(AiSelectors.isMultipleImageUploadAllowed)
  currentAgent = this.#store.selectSignal(AiSelectors.selectedAgent)

  items = this.getMenuItems()

  isMultipleUploadAllowed = this.#store.selectSignal(AiSelectors.isMultipleUploadAllowed)

  uploadFileTooltip = computed(() => {
    const allowedFileTypes = this.allowedFileTypes().filter((type) => type !== FileExtension.BPMN)

    if (this.isUploadDisabled()) {
      return ''
    }

    const allowedFileTypesStr =
      allowedFileTypes.length === 1
        ? FileMimeTypeLabel[allowedFileTypes[0]]
        : `${slice(0, -2, allowedFileTypes)
            .map((type) => FileMimeTypeLabel[type])
            .join(', ')} and ${FileMimeTypeLabel[last(allowedFileTypes)]}`

    return `Upload ${allowedFileTypesStr} files to use as context for your prompt.`
  })

  get promptInvalidLengthMsg(): string {
    const { actualLength, requiredLength } = this.typeControl.errors?.maxlength || {}

    return this.typeControl.errors?.maxlength
      ? `Character Limit Exceeded ${actualLength} / ${requiredLength}`
      : null
  }

  #waitingForHumanApproval = this.#store.selectSignal(AiSelectors.waitingForHumanApproval)

  constructor() {
    this.#onCopyToChatInputField()

    effect(() => {
      const bpmnFilesSelected = this.#bpmnFilesSelected()
      const waitingForApproval = this.#waitingForHumanApproval()
      if (bpmnFilesSelected || waitingForApproval) {
        this.typeControl.disable()
      } else {
        this.typeControl.enable()
      }
    })
  }

  ngOnInit(): void {
    this.typeControl?.valueChanges
      .pipe(
        debounceTime(300), // Wait 300ms after typing stops
        distinctUntilChanged(), // Only emit if value is different from last emitted value
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((value) => {
        this.currentValue.emit(value)
      })
  }

  filterKeyEvents(event: KeyboardEvent): void {
    const { key, shiftKey, metaKey } = event
    const submitKeyEv = key.toLowerCase() === 'enter' && !shiftKey && !metaKey
    const newLineKeyEv = key.toLowerCase() === 'enter' && metaKey

    if (newLineKeyEv) {
      this.typeControl.setValue(this.typeControl.value + '\n')
    }

    if (submitKeyEv) {
      event.preventDefault()
      event.stopPropagation()
      this.submitTypeControl()
    }
  }

  onTypeAreaValue(event: Event): void {
    const textareaValue = (event.target as HTMLTextAreaElement).value
    this.currentValue.emit(textareaValue)
  }

  getMenuItems(): any[] {
    switch (this.currentAgent()) {
      case 'designer':
        return [
          {
            label: 'Image',
            icon: 'ph ph-image',
            description: 'A design screenshot or just a sketch on a napkin',
            command: () => {
              this.imageUpload().nativeElement.click()
            },
          },
        ]
      case 'analyst':
        return [
          {
            label: 'BPMN Process',
            icon: 'ph ph-code',
            description: 'Import an existing BPMN Process',
            command: () => {
              this.processUpload().nativeElement.click()
            },
          },
          {
            label: 'Document',
            icon: 'ph ph-file-pdf',
            description: 'A business process document',
            command: () => {
              this.docUpload().nativeElement.click()
            },
          },
          {
            label: 'Image',
            icon: 'ph ph-image',
            description: 'A process screenshot',
            command: () => {
              this.imageUpload().nativeElement.click()
            },
          },
        ]
      default:
        return [
          {
            label: 'Document',
            icon: 'ph ph-file-pdf',
            description: 'A PDF document',
            command: () => {
              this.docUpload().nativeElement.click()
            },
          },
        ]
    }
  }

  // TODO: Refactor this to be a computed signal
  canSubmitPrompt(): boolean {
    const imagesSelected = !!this.#imageFiles().length
    const bpmnFilesSelected = this.#bpmnFilesSelected()
    const textValid = this.typeControl.valid
    const aiPending = this.#store.selectSignal(AiSelectors.pendingAiResponse)()

    return (imagesSelected || bpmnFilesSelected || textValid) && !aiPending
  }

  // TODO: Rename imageFiles to files everywhere (components and most probably store)
  submitTypeControl(): void {
    if (this.canSubmitPrompt()) {
      this.#store.dispatch(
        AiActions.typeAreaSubmit({
          text: this.typeControl.value,
          files: [...this.#imageFiles(), ...this.#bpmnFiles()].map((file) => file.file),
        })
      )
      this.#resetAndFocusTextarea()
    }
  }

  onFileSelected(event: Event): void {
    this.#store.dispatch(AiActions.fileUploaded({ event }))
    resetInputFileValueFromEvent(event)
  }

  #onCopyToChatInputField(): void {
    this.#actions$
      .pipe(ofType(AiActions.copyToChatInputField), takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ text }) => {
        this.typeControl.setValue(text)
      })
  }

  #resetAndFocusTextarea(): void {
    this.typeControl.reset()
    this.textarea()?.nativeElement.focus()
  }
}
